import { Suspense, lazy } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AuthGate } from './auth/AuthGate';
import { LoginPage } from './auth/LoginPage';
import { OnboardingPage } from './auth/OnboardingPage';
import { SignupPage } from './auth/SignupPage';
import { AuthProvider } from './auth/useAuthContext';
import { AskAiPanel } from './components/AskAiPanel';
import { ErrorBoundary } from './components/ErrorBoundary';
import { LiveStayPage } from './customer/pages/LiveStayPage';
import { BookingsNew } from './pages/BookingsNew';
import { Calendar } from './pages/Calendar';
import { CopilotPage } from './pages/CopilotPage';
import { CustomerDetail } from './pages/CustomerDetail';
import { CustomerEdit } from './pages/CustomerEdit';
import { CustomerNew } from './pages/CustomerNew';
import { Customers } from './pages/Customers';
import { History } from './pages/History';
import { KiosksAdmin } from './pages/KiosksAdmin';
import { NotFoundPage } from './pages/NotFoundPage';
import { PetEdit } from './pages/PetEdit';
import { PetNew } from './pages/PetNew';
import { TaskBoard } from './pages/TaskBoard';
import { Today } from './pages/Today';
import { VetDirectoryPage } from './pages/VetDirectoryPage';
import { Waitlist } from './pages/Waitlist';
import { CheckinPage } from './pages/checkin/CheckinPage';
import { KioskSetup } from './pages/checkin/KioskSetup';
import { DeliveriesPage } from './pages/communications/DeliveriesPage';
import { FailuresPage } from './pages/communications/FailuresPage';
import { InboxPage } from './pages/communications/InboxPage';
import { TemplateEditPage } from './pages/communications/TemplateEditPage';
import { TemplatesPage } from './pages/communications/TemplatesPage';
import { CampaignsPage } from './pages/marketing/CampaignsPage';
import { RefundRequestsPage } from './pages/refunds/RefundRequestsPage';
import { ReportsPage } from './pages/reports/ReportsPage';
import { AccountSettings } from './pages/settings/AccountSettings';
import { ApiKeysSettings } from './pages/settings/ApiKeysSettings';
import { AssessmentsSettings } from './pages/settings/AssessmentsSettings';
import { BillingSettings } from './pages/settings/BillingSettings';
import { BookingStatusesPage } from './pages/settings/BookingStatusesPage';
import { BrandingSettings } from './pages/settings/BrandingSettings';
import { CancellationPolicySettings } from './pages/settings/CancellationPolicySettings';
import { ComplianceSettings } from './pages/settings/ComplianceSettings';
import { CopilotSettingsPage } from './pages/settings/CopilotSettings';
import { CustomDomainSettings } from './pages/settings/CustomDomainSettings';
import { CustomFieldsSettings } from './pages/settings/CustomFieldsSettings';
import { InsuranceSettings } from './pages/settings/InsuranceSettings';
import { IntakeQuestionsPage } from './pages/settings/IntakeQuestionsPage';
import { IntegrationsPage } from './pages/settings/IntegrationsPage';
import { ReportCardsSettings } from './pages/settings/ReportCardsSettings';
import { SettingsIndex } from './pages/settings/SettingsIndex';
import { SettingsLayout } from './pages/settings/SettingsLayout';
import { TagsSettingsPage } from './pages/settings/TagsSettingsPage';
import { TaskBoardSettingsPage } from './pages/settings/TaskBoardSettingsPage';
import { VaccinePoliciesSettings } from './pages/settings/VaccinePoliciesSettings';
import { VaccinesSettings } from './pages/settings/VaccinesSettings';
import { VetsSettingsPage } from './pages/settings/VetsSettingsPage';
import { FacilityCamerasPage } from './pages/settings/integrations/FacilityCamerasPage';
import { GbpSettings } from './pages/settings/integrations/GbpSettings';
import { MicrochipSettings } from './pages/settings/integrations/MicrochipSettings';
import { OpsAlertsSettings } from './pages/settings/integrations/OpsAlertsSettings';
import { WebhooksSettings } from './pages/settings/integrations/WebhooksSettings';
import { PostcardsSettings } from './pages/settings/marketing/PostcardsSettings';
import { ScheduledReportsPage } from './pages/settings/scheduled-reports/ScheduledReportsPage';
import { TransportPage } from './pages/transport/TransportPage';

// TipTap-using pages are lazy-loaded so unit tests rendering the full App
// don't pull in the @tiptap chain (which has an ESM-export shape that
// vitest's jsdom transformer doesn't resolve cleanly).
const RunCardEditorPage = lazy(() =>
  import('./pages/settings/RunCardEditorPage').then((m) => ({ default: m.RunCardEditorPage })),
);
const WaiverSettings = lazy(() =>
  import('./pages/settings/WaiverSettings').then((m) => ({ default: m.WaiverSettings })),
);
import { BookingDetailPage } from './pages/BookingDetailPage';
import { BookingWizard } from './pages/BookingWizard';
import { HealthMonitorPage } from './pages/HealthMonitorPage';
import { PetDetailPage } from './pages/PetDetailPage';
import { VaccineOverridesQueuePage } from './pages/VaccineOverridesQueuePage';
import { IncidentDetailPage } from './pages/incidents/IncidentDetailPage';
import { IncidentsPage } from './pages/incidents/IncidentsPage';
import { NewIncidentPage } from './pages/incidents/NewIncidentPage';
import { PosCustomerDisplayPage } from './pages/pos/PosCustomerDisplayPage';
import { PosTillPage } from './pages/pos/PosTillPage';
import { RecurringSeriesPage } from './pages/recurring/RecurringSeriesPage';
import { BoardingOverstays } from './pages/settings/BoardingOverstays';
import { BuildingsSettings } from './pages/settings/BuildingsSettings';
import { DataImport } from './pages/settings/DataImport';
import { DaycareCapacity } from './pages/settings/DaycareCapacity';
import { DaycareLatePickup } from './pages/settings/DaycareLatePickup';
import { DiscountsSettings } from './pages/settings/DiscountsSettings';
import { EmailSendingDomain } from './pages/settings/EmailSendingDomain';
import { FacilitiesSettingsPage } from './pages/settings/FacilitiesSettingsPage';
import { GiftCardsSettings } from './pages/settings/GiftCardsSettings';
import { LoyaltyProgramSettings } from './pages/settings/LoyaltyProgramSettings';
import { MembershipsSettings } from './pages/settings/MembershipsSettings';
import { PackagesSettings } from './pages/settings/PackagesSettings';
import { PaymentsSettings } from './pages/settings/PaymentsSettings';
import { PricingRulesSettings } from './pages/settings/PricingRulesSettings';
import { RunsSettings } from './pages/settings/RunsSettings';
import { ServicesSettings } from './pages/settings/ServicesSettings';
import { SmsProvider } from './pages/settings/SmsProvider';
import { StaffScheduleSettings } from './pages/settings/StaffScheduleSettings';
import { TeamRoles } from './pages/settings/TeamRoles';
import { TeamSettings } from './pages/settings/TeamSettings';
import { TransportSettingsPage } from './pages/settings/TransportSettingsPage';
import { VehiclesSettings } from './pages/settings/VehiclesSettings';
import { GroomerDetailPage } from './pages/settings/grooming/GroomerDetailPage';
import { GroomersListPage } from './pages/settings/grooming/GroomersListPage';
import { PosPrinterSettings } from './pages/settings/pos/PosPrinterSettings';
import { PosProductsSettings } from './pages/settings/pos/PosProductsSettings';
import { PosSessionsSettings } from './pages/settings/pos/PosSessionsSettings';
import { PosStockSettings } from './pages/settings/pos/PosStockSettings';
import { AppShell } from './shell/AppShell';

export function StaffApp() {
  return (
    <ErrorBoundary context="staff">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            {/* Public */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />

            {/* Kiosk: token-only auth, no AuthGate */}
            <Route path="/checkin" element={<CheckinPage />} />
            <Route path="/checkin/setup" element={<KioskSetup />} />

            {/* Live stay: public token-auth page accessible on all hosts */}
            <Route path="/stay/:token" element={<LiveStayPage />} />

            {/* Onboarding — gated by AuthGate but renders without AppShell chrome */}
            <Route
              path="/onboarding/*"
              element={
                <AuthGate>
                  <OnboardingPage />
                </AuthGate>
              }
            />

            {/* Staff — protected */}
            <Route
              element={
                <AuthGate>
                  <AppShell />
                </AuthGate>
              }
            >
              <Route index element={<Navigate to="/today" replace />} />
              <Route path="/today" element={<Today />} />
              <Route path="/health" element={<HealthMonitorPage />} />
              <Route path="/tasks" element={<TaskBoard />} />
              <Route path="/history" element={<History />} />
              <Route path="/bookings/new" element={<BookingWizard />} />
              <Route path="/bookings/new/legacy" element={<BookingsNew />} />
              <Route path="/bookings/:id" element={<BookingDetailPage />} />
              <Route path="/recurring-series/:id" element={<RecurringSeriesPage />} />
              <Route path="/customers" element={<Customers />} />
              <Route path="/vets" element={<VetDirectoryPage />} />
              <Route path="/customers/new" element={<CustomerNew />} />
              <Route path="/customers/:id" element={<CustomerDetail />} />
              <Route path="/customers/:id/edit" element={<CustomerEdit />} />
              <Route path="/customers/:customerId/pets/new" element={<PetNew />} />
              <Route path="/customers/:customerId/pets/:petId" element={<PetDetailPage />} />
              <Route path="/customers/:customerId/pets/:petId/edit" element={<PetEdit />} />
              <Route path="/calendar" element={<Calendar />} />
              <Route path="/waitlist" element={<Waitlist />} />
              <Route path="/pos" element={<PosTillPage />} />
              <Route path="/pos/customer-display" element={<PosCustomerDisplayPage />} />
              <Route path="/refund-requests" element={<RefundRequestsPage />} />
              <Route path="/transport" element={<TransportPage />} />
              <Route path="/reports" element={<ReportsPage />} />
              <Route path="/reports/:reportType" element={<ReportsPage />} />
              <Route path="/copilot" element={<CopilotPage />} />
              <Route path="/marketing" element={<Navigate to="/marketing/campaigns" replace />} />
              <Route path="/marketing/campaigns" element={<CampaignsPage />} />
              {/* Communications — sub-spec 03 + template editor */}
              <Route
                path="/communications"
                element={<Navigate to="/communications/inbox" replace />}
              />
              <Route path="/communications/inbox" element={<InboxPage />} />
              <Route path="/communications/deliveries" element={<DeliveriesPage />} />
              <Route path="/communications/failures" element={<FailuresPage />} />
              <Route path="/communications/templates" element={<TemplatesPage />} />
              <Route
                path="/communications/templates/:key/:channel/:locale"
                element={<TemplateEditPage />}
              />
              <Route path="/vaccine-overrides" element={<VaccineOverridesQueuePage />} />
              {/* Incidents */}
              <Route path="/incidents" element={<IncidentsPage />} />
              <Route path="/incidents/new" element={<NewIncidentPage />} />
              <Route path="/incidents/:id" element={<IncidentDetailPage />} />

              {/* Settings — all wrapped in SettingsLayout for consistent sidebar + content shell */}
              <Route path="/settings" element={<SettingsLayout />}>
                <Route index element={<SettingsIndex />} />
                <Route path="account" element={<AccountSettings />} />
                <Route path="branding" element={<BrandingSettings />} />
                <Route path="custom-domain" element={<CustomDomainSettings />} />
                <Route path="integrations" element={<IntegrationsPage />} />
                <Route path="integrations/microchip" element={<MicrochipSettings />} />
                <Route path="integrations/ip_camera" element={<FacilityCamerasPage />} />
                <Route path="integrations/cameras" element={<FacilityCamerasPage />} />
                <Route path="integrations/gbp" element={<GbpSettings />} />
                <Route path="integrations/zapier" element={<WebhooksSettings />} />
                <Route path="integrations/webhooks" element={<WebhooksSettings />} />
                <Route path="ops-alerts" element={<OpsAlertsSettings />} />
                <Route path="integrations/ops-alerts" element={<OpsAlertsSettings />} />
                <Route path="api-keys" element={<ApiKeysSettings />} />
                <Route path="insurance" element={<InsuranceSettings />} />
                <Route path="compliance" element={<ComplianceSettings />} />
                <Route path="report-cards" element={<ReportCardsSettings />} />
                <Route path="assessments" element={<AssessmentsSettings />} />
                <Route path="email-sending-domain" element={<EmailSendingDomain />} />
                <Route path="sms-provider" element={<SmsProvider />} />
                <Route path="buildings" element={<BuildingsSettings />} />
                <Route path="runs" element={<RunsSettings />} />
                <Route path="services" element={<ServicesSettings />} />
                <Route path="pricing-rules" element={<PricingRulesSettings />} />
                <Route path="cancellation-policy" element={<CancellationPolicySettings />} />
                <Route path="discounts" element={<DiscountsSettings />} />
                <Route path="import" element={<DataImport />} />
                <Route path="loyalty" element={<LoyaltyProgramSettings />} />
                <Route path="gift-cards" element={<GiftCardsSettings />} />
                <Route path="packages" element={<PackagesSettings />} />
                <Route path="grooming/groomers" element={<GroomersListPage />} />
                <Route path="grooming/groomers/:id" element={<GroomerDetailPage />} />
                <Route path="daycare/capacity" element={<DaycareCapacity />} />
                <Route path="daycare/late-pickup" element={<DaycareLatePickup />} />
                <Route path="boarding/overstays" element={<BoardingOverstays />} />
                <Route path="pos/products" element={<PosProductsSettings />} />
                <Route path="pos/printer" element={<PosPrinterSettings />} />
                <Route path="pos/sessions" element={<PosSessionsSettings />} />
                <Route path="pos/stock" element={<PosStockSettings />} />
                <Route path="memberships" element={<MembershipsSettings />} />
                <Route path="team" element={<TeamSettings />} />
                <Route path="team/roles" element={<TeamRoles />} />
                <Route
                  path="waiver"
                  element={
                    <Suspense
                      fallback={<div className="py-8 text-sm text-muted-foreground">Loading…</div>}
                    >
                      <WaiverSettings />
                    </Suspense>
                  }
                />
                <Route path="billing" element={<BillingSettings />} />
                <Route path="payments" element={<PaymentsSettings />} />
                <Route path="kiosks" element={<KiosksAdmin />} />
                <Route path="custom-fields" element={<CustomFieldsSettings />} />
                <Route
                  path="run-card"
                  element={
                    <Suspense
                      fallback={<div className="py-8 text-sm text-muted-foreground">Loading…</div>}
                    >
                      <RunCardEditorPage />
                    </Suspense>
                  }
                />
                <Route path="intake-questions" element={<IntakeQuestionsPage />} />
                <Route path="vaccines" element={<VaccinesSettings />} />
                <Route path="vaccines/policies" element={<VaccinePoliciesSettings />} />
                <Route path="booking-statuses" element={<BookingStatusesPage />} />
                <Route path="task-board" element={<TaskBoardSettingsPage />} />
                <Route path="tags" element={<TagsSettingsPage />} />
                <Route path="vets" element={<VetsSettingsPage />} />
                <Route path="scheduled-reports" element={<ScheduledReportsPage />} />
                <Route path="marketing/postcards" element={<PostcardsSettings />} />
                <Route path="facilities" element={<FacilitiesSettingsPage />} />
                <Route path="transport" element={<TransportSettingsPage />} />
                <Route path="vehicles" element={<VehiclesSettings />} />
                <Route path="copilot" element={<CopilotSettingsPage />} />
                {/* Staff rota — migration 0085 */}
                <Route path="staff-schedule" element={<StaffScheduleSettings />} />
              </Route>
            </Route>

            {/* Branded 404 — catches every unknown URL inside the staff app. */}
            <Route path="*" element={<NotFoundPage context="staff" />} />
          </Routes>
          {/* Global Ask AI slide-over — opens via `?` / `/` keyboard shortcut. */}
          <AskAiPanel />
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
